import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Circle, Mic, MicOff, PhoneOff, Square, Video, VideoOff } from 'lucide-react';
import toast from 'react-hot-toast';
import './VideoCall.css';

const stamp = () => new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19);
const safeName = (text) => String(text || 'call').trim().replace(/[^a-z0-9]+/gi, '_').slice(0, 40) || 'call';
const download = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = fileName;
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
};

/**
 * The admin's call screen: the other person large, yourself small, and the buttons, including
 * Screenshot (saves a picture of the call) and Record (saves a video of the call with both voices).
 * The other person is told while a recording is on (onRecordingChange).
 */
const CallWindow = ({ title, status, note, remoteStream, localStream, onHangup, onRecordingChange, onSaveMedia }) => {
    const remoteRef = useRef(null);
    const localRef = useRef(null);
    const [micOn, setMicOn] = useState(true);
    const [camOn, setCamOn] = useState(true);
    const [seconds, setSeconds] = useState(0);
    const [recording, setRecording] = useState(false);
    const [recSeconds, setRecSeconds] = useState(0);
    const recRef = useRef(null); // { recorder, timer, audio }
    const saveRef = useRef(onSaveMedia);
    useEffect(() => { saveRef.current = onSaveMedia; }, [onSaveMedia]);

    // saved on the server in the person's own folder (Call recordings page); if that fails the file is downloaded instead
    const keep = async (kind, blob, fileName, durationSec) => {
        if (saveRef.current) {
            try { await saveRef.current({ kind, blob, fileName, durationSec }); return; } catch { toast.error('Could not save on the server. Downloading it instead.'); }
        }
        download(blob, fileName);
    };

    useEffect(() => { if (remoteRef.current) remoteRef.current.srcObject = remoteStream || null; }, [remoteStream]);
    useEffect(() => { if (localRef.current) localRef.current.srcObject = localStream || null; }, [localStream]);
    useEffect(() => {
        if (status !== 'connected') return undefined;
        const started = Date.now();
        const timer = setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
        return () => clearInterval(timer);
    }, [status]);

    const toggle = (kind) => {
        const tracks = kind === 'mic' ? localStream?.getAudioTracks() : localStream?.getVideoTracks();
        const next = !(kind === 'mic' ? micOn : camOn);
        tracks?.forEach(track => { track.enabled = next; });
        if (kind === 'mic') setMicOn(next); else setCamOn(next);
    };

    // a picture of the other person (with yourself in the corner) as a PNG file
    const screenshot = () => {
        const remote = remoteRef.current;
        if (!remote || !remote.videoWidth) { toast.error('There is no video to capture yet.'); return; }
        const canvas = document.createElement('canvas');
        canvas.width = remote.videoWidth; canvas.height = remote.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(remote, 0, 0, canvas.width, canvas.height);
        const local = localRef.current;
        if (local && local.videoWidth && camOn) {
            const w = Math.round(canvas.width * 0.22); const h = Math.round(w * (local.videoHeight / local.videoWidth));
            ctx.drawImage(local, canvas.width - w - 16, canvas.height - h - 16, w, h);
        }
        canvas.toBlob((blob) => {
            if (!blob) { toast.error('Could not save the picture.'); return; }
            keep('screenshot', blob, `call-${safeName(title)}-${stamp()}.png`, 0);
        }, 'image/png');
    };

    const stopRecording = useCallback((silent) => {
        const rec = recRef.current;
        if (!rec) return;
        recRef.current = null;
        clearInterval(rec.timer);
        try { if (rec.recorder.state !== 'inactive') rec.recorder.stop(); } catch { /* already stopped */ }
        try { rec.audio.close(); } catch { /* ignore */ }
        setRecording(false); setRecSeconds(0);
        onRecordingChange?.(false);
    }, [onRecordingChange]);

    // the picture of both people drawn on a canvas, plus both voices mixed: saved as a .webm video
    const startRecording = () => {
        const remote = remoteRef.current; const local = localRef.current;
        if (!remote || !remote.videoWidth || !remoteStream) { toast.error('Wait until the person is connected to start recording.'); return; }
        if (typeof MediaRecorder === 'undefined') { toast.error('This browser cannot record. Use Chrome or Edge.'); return; }
        const W = 1280; const H = 720;
        const canvas = document.createElement('canvas'); canvas.width = W; canvas.height = H;
        const ctx = canvas.getContext('2d');
        const draw = () => {
            ctx.fillStyle = '#000'; ctx.fillRect(0, 0, W, H);
            if (remote.videoWidth) {
                const scale = Math.min(W / remote.videoWidth, H / remote.videoHeight);
                const w = remote.videoWidth * scale; const h = remote.videoHeight * scale;
                ctx.drawImage(remote, (W - w) / 2, (H - h) / 2, w, h);
            }
            if (local && local.videoWidth && camOn) {
                const w = 240; const h = Math.round(w * (local.videoHeight / local.videoWidth));
                ctx.drawImage(local, W - w - 20, H - h - 20, w, h);
            }
        };
        draw();
        const drawTimer = setInterval(draw, 40);

        const audio = new (window.AudioContext || window.webkitAudioContext)();
        const mix = audio.createMediaStreamDestination();
        [remoteStream, localStream].forEach((stream) => {
            if (stream && stream.getAudioTracks().length) audio.createMediaStreamSource(new MediaStream(stream.getAudioTracks())).connect(mix);
        });
        const out = new MediaStream([...canvas.captureStream(25).getVideoTracks(), ...mix.stream.getAudioTracks()]);
        const mime = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'].find(m => MediaRecorder.isTypeSupported(m));
        const recorder = new MediaRecorder(out, mime ? { mimeType: mime, videoBitsPerSecond: 2_500_000 } : undefined);
        const chunks = [];
        const fileName = `call-${safeName(title)}-${stamp()}.webm`;
        recorder.ondataavailable = (event) => { if (event.data && event.data.size) chunks.push(event.data); };
        const started = Date.now();
        recorder.onstop = () => {
            clearInterval(drawTimer);
            if (chunks.length) keep('recording', new Blob(chunks, { type: 'video/webm' }), fileName, (Date.now() - started) / 1000);
        };
        recorder.start(1000);
        const timer = setInterval(() => setRecSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
        recRef.current = { recorder, timer, audio };
        setRecording(true);
        onRecordingChange?.(true);
    };

    // the call ends while recording: the recording is saved
    useEffect(() => () => { if (recRef.current) stopRecording(true); }, [stopRecording]);

    const mmss = (n) => `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
    const label = { calling: 'Calling…', ringing: 'Ringing…', connecting: 'Connecting…', connected: mmss(seconds), ended: 'Call ended' }[status] || status;
    const canCapture = status === 'connected';

    return (
        <div className="vc-screen" role="dialog" aria-modal="true" aria-label="Video call">
            <div className="vc-remote">
                <video ref={remoteRef} autoPlay playsInline className={remoteStream ? 'is-live' : ''} />
                {!remoteStream && <div className="vc-wait"><div className="vc-avatar">{(title || '?').trim().charAt(0).toUpperCase()}</div></div>}
            </div>
            <div className="vc-top"><strong>{title}</strong><span>{label}</span>{!!note && <small>{note}</small>}</div>
            {recording && <div className="vc-rec"><i />REC {mmss(recSeconds)}</div>}
            <video ref={localRef} autoPlay playsInline muted className={`vc-local ${camOn ? '' : 'is-off'}`} />
            <div className="vc-bar">
                <button type="button" className={micOn ? '' : 'is-off'} onClick={() => toggle('mic')} aria-label={micOn ? 'Mute microphone' : 'Unmute microphone'}>{micOn ? <Mic size={22} /> : <MicOff size={22} />}</button>
                <button type="button" onClick={screenshot} disabled={!canCapture} title="Take a screenshot" aria-label="Take a screenshot"><Camera size={22} /></button>
                <button type="button" className="vc-hang" onClick={onHangup} aria-label="End call"><PhoneOff size={26} /></button>
                <button type="button" className={recording ? 'is-recording' : ''} onClick={() => (recording ? stopRecording(false) : startRecording())} disabled={!canCapture && !recording} title={recording ? 'Stop recording' : 'Record the call'} aria-label={recording ? 'Stop recording' : 'Record the call'}>{recording ? <Square size={20} fill="currentColor" /> : <Circle size={22} />}</button>
                <button type="button" className={camOn ? '' : 'is-off'} onClick={() => toggle('cam')} aria-label={camOn ? 'Turn camera off' : 'Turn camera on'}>{camOn ? <Video size={22} /> : <VideoOff size={22} />}</button>
            </div>
        </div>
    );
};

export default CallWindow;
